import { useState, useId, useRef, useEffect } from 'react';
import type { ReactNode, MouseEvent, ButtonHTMLAttributes } from 'react';
import { useKeyPressEvent, useMedia } from 'react-use';
import { usePopper } from 'react-popper';
import type { Placement } from '@popperjs/core';
import { getBreakpoint } from '../../Theme';
import { Overlay } from '../Overlay';
import { TooltipTrigger, TooltipContent, TooltipArrow } from './Tooltip.styles';

const TOOLTIP_OFFSET_PX = 16;

type TooltipProps = {
  title?: string;
  trigger: ReactNode;
  content: ReactNode;
  width?: 'default' | 'wide';
  position?: Placement;
} & Pick<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'aria-label' | 'aria-labelledby' | 'className'
>;

const Tooltip = ({
  content,
  trigger,
  title,
  width = 'default',
  position = 'bottom',
  ...rest
}: TooltipProps) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const id = `tooltip-content-${useId()}`;
  const ref = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<HTMLDivElement>(null);

  const { styles, attributes, update } = usePopper(
    ref.current,
    tooltipRef.current,
    {
      placement: position,
      strategy: 'fixed',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset:
              position === 'left' || position === 'right'
                ? [0, TOOLTIP_OFFSET_PX + 6]
                : [0, TOOLTIP_OFFSET_PX],
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['top', 'bottom'],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            padding: TOOLTIP_OFFSET_PX,
          },
        },
        {
          name: 'arrow',
          options: {
            element: arrowRef.current,
            padding: TOOLTIP_OFFSET_PX * -1,
          },
        },
      ],
    },
  );

  const isMobile = useMedia(`(max-width: ${getBreakpoint('lg')}px)`, false);

  // workaround to update tooltip position after layout change (e.g. feature toggle)
  useEffect(() => {
    if (showTooltip && ref.current) {
      update?.();
    }
  }, [showTooltip, update]);

  useKeyPressEvent('Escape', () => {
    if (showTooltip) {
      setShowTooltip(false);
    }
  });

  const handleTooltipToggle = (event?: MouseEvent) => {
    event?.stopPropagation();
    setShowTooltip(prev => !prev);
  };

  const handleTooltipVisibility = (visible: boolean) => {
    if (!isMobile) {
      setShowTooltip(visible);
    }
  };
  return (
    <div
      onMouseEnter={() => handleTooltipVisibility(true)}
      onMouseLeave={() => handleTooltipVisibility(false)}
    >
      <TooltipTrigger
        type="button"
        ref={ref}
        aria-describedby={id}
        aria-haspopup={isMobile ? 'dialog' : undefined}
        onClick={handleTooltipToggle}
        onBlur={() => handleTooltipVisibility(false)}
        $hover={!isMobile}
        {...rest}
      >
        {trigger}
      </TooltipTrigger>

      {isMobile ? (
        <Overlay
          id={id}
          open={showTooltip}
          title={title}
          onClose={() => setShowTooltip(false)}
        >
          {content}
        </Overlay>
      ) : (
        <TooltipContent
          role="tooltip"
          id={id}
          $width={width}
          $visible={showTooltip}
          ref={tooltipRef}
          style={styles.popper}
          {...attributes.popper}
        >
          <TooltipArrow ref={arrowRef} style={styles.arrow} data-popper-arrow />
          {content}
        </TooltipContent>
      )}
    </div>
  );
};

export { Tooltip };
